import React, { useCallback } from 'react';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { BaseFormContainer, FormInput } from './common';
import { ManufacturerSelector } from './ManufacturerSelector';
import { VehicleTypeSelector } from './VehicleTypeSelector';
import { PowertrainSelector } from './PowertrainSelector';
import { VehicleTagSelector } from './VehicleTagSelector';
import { Accordion } from '../ui/Accordion';
import { VehiclePowertrain, VehicleTag } from '../../types';
import { useFormValidation, commonValidationRules } from '../../hooks/useFormValidation';

interface VehicleModelFormData extends Record<string, unknown> {
  manufacturerId: string;
  vehicleTypeId: string;
  modelName: string;
  // 新規フィールド（任意）
  powertrain?: VehiclePowertrain;
  vehicleTags?: VehicleTag[];
}

interface VehicleModelFormProps {
  formData: VehicleModelFormData;
  editingModel: { id: string; name: string; manufacturerId: string; vehicleTypeId: string } | null;
  manufacturers: Array<{ id: string; name: string }>;
  vehicleTypes: Array<{ id: string; name: string }>;
  isLoading: boolean;
  onInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export const VehicleModelForm: React.FC<VehicleModelFormProps> = React.memo(
  ({
    formData,
    editingModel,
    manufacturers: _manufacturers,
    vehicleTypes: _vehicleTypes,
    isLoading,
    onInputChange,
    onSubmit,
    onCancel,
  }) => {
    // フォームバリデーション設定
    const validationConfig = {
      manufacturerId: { required: true },
      vehicleTypeId: { required: true },
      modelName: {
        required: true,
        rules: [commonValidationRules.minLength(1), commonValidationRules.maxLength(100)],
      },
    };

    const { errors, validateFieldIfTouched } = useFormValidation(validationConfig);

    // 統一フィールド変更ハンドラー
    const handleFieldChange = useCallback(
      (
        name: string,
        value: string | number | VehiclePowertrain | VehicleTag[] | undefined | null
      ) => {
        const syntheticEvent = {
          target: { name, value },
        } as React.ChangeEvent<HTMLInputElement | HTMLSelectElement>;
        onInputChange(syntheticEvent);

        // リアルタイムバリデーション
        validateFieldIfTouched(name, value, formData);
      },
      [onInputChange, validateFieldIfTouched, formData]
    );

    return (
      <BaseFormContainer
        title={editingModel ? '車両モデル情報編集' : '新規車両モデル登録'}
        icon={<PlusCircleIcon />}
        onSubmit={onSubmit}
        onCancel={onCancel}
        isLoading={isLoading}
        submitLabel={editingModel ? '更新' : '車両モデルを登録'}
        isEdit={!!editingModel}
      >
        {/* 基本フィールド */}
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6'>
          <ManufacturerSelector
            value={formData.manufacturerId}
            onChange={manufacturerId => handleFieldChange('manufacturerId', manufacturerId)}
            label='メーカー'
            placeholder='メーカーを選択してください'
            required
            disabled={isLoading}
            error={errors.manufacturerId}
          />

          <VehicleTypeSelector
            value={formData.vehicleTypeId}
            onChange={vehicleTypeId => handleFieldChange('vehicleTypeId', vehicleTypeId)}
            label='車種タイプ'
            placeholder='車種タイプを選択してください'
            required
            disabled={isLoading}
            error={errors.vehicleTypeId}
          />
        </div>

        {/* 詳細設定（任意） */}
        <Accordion
          title='詳細設定（任意）'
          defaultOpen={false}
          disabled={isLoading}
          className='mt-6'
        >
          <div className='space-y-4'>
            {/* パワートレイン（任意） */}
            <PowertrainSelector
              value={formData.powertrain}
              onChange={powertrain => handleFieldChange('powertrain', powertrain)}
              label='パワートレイン'
              placeholder='パワートレインを選択してください'
              disabled={isLoading}
            />

            {/* 車両タグ（任意） */}
            <VehicleTagSelector
              value={formData.vehicleTags || []}
              onChange={tags => handleFieldChange('vehicleTags', tags)}
              label='車両タグ'
              disabled={isLoading}
            />
          </div>
        </Accordion>

        {/* 車両モデル名（最下部） */}
        <FormInput
          name='modelName'
          label='車両モデル名'
          value={formData.modelName}
          onChange={handleFieldChange}
          placeholder='例: プリウス、ノート、エルフ'
          required
          disabled={isLoading}
          error={errors.modelName}
          helperText='メーカー名を除いたモデル名のみを入力してください'
        />
      </BaseFormContainer>
    );
  }
);

VehicleModelForm.displayName = 'VehicleModelForm';

export default VehicleModelForm;
